import React, { useContext } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  PropTypes,
  Typography,
  SvgIconProps,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { keyValuePairs } from "../../types";
import theme from "common/theme/theme";
import { LanguageContext } from "common/contexts/LanguageContext";

export interface IDialogTitleProps {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

export interface ICommand {
  color: PropTypes.Color;
  autoFocus: boolean;
  label: string;
  labelIcon?: React.ComponentType<SvgIconProps>;
  canExecute: boolean;
  execute: (data: keyValuePairs) => void;
  disabled?: boolean;
  hidden?: boolean;
  variant: "contained" | "outlined"
}

export type JustifyContent =
  | "flex-start"
  | "flex-end"
  | "center"
  | "space-between"
  | "space-around"
  | "space-evenly";

export interface IConfirmationModalProps {
  children?: React.ReactNode;
  title: string;
  maxWidth: any;
  isOpen: boolean;
  confirmBtnLabel?: string;
  confirmBtnIcon?: React.ComponentType<SvgIconProps>;
  handleConfirm: any;
  handleClose: () => void;
  testId?: string;
  hideCancel?: boolean;
  hideDivider?: boolean;
  buttonsJustify?: JustifyContent;
}

const MyDialogTitle = (props: IDialogTitleProps) => {
  const { children, onClose, ...other } = props;
  return (
    <DialogTitle
      sx={{
        margin: 0,
        padding: theme.spacing(1, 3),
        backgroundColor: theme.accent.light,
        color: theme.secret.darkgray,
      }}
      {...other}
    >
      {/* disableTypography className={classes.root} {...other}> */}
      <Typography variant="dialogtitle" sx={{ marginBottom: "0" }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          sx={{
            position: "absolute",
            right: theme.spacing(0.5),
            top: theme.spacing(0.5),
            color: theme.secret.darkgray,
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

export default function ConfirmationModal(props: IConfirmationModalProps) {
  const {
    children,
    title,
    maxWidth,
    isOpen,
    confirmBtnLabel,
    confirmBtnIcon,
    handleConfirm,
    handleClose,
    testId,
    hideCancel,
    hideDivider,
    buttonsJustify,
  } = props;
  const { getString } = useContext(LanguageContext);

  const dialogActions: ICommand[] = [
    {
      autoFocus: true,
      color: "primary",
      label: confirmBtnLabel ?? getString('cmn_confirm'),
      labelIcon: confirmBtnIcon,
      canExecute: true,
      execute: handleConfirm,
      variant: "contained",
    },
    {
      autoFocus: false,
      color: "primary",
      label: getString('cmn_cancel'),
      canExecute: true,
      hidden: hideCancel,
      execute: handleClose,
      variant: "outlined"
    },
  ];

  return (
    <div>
      <Dialog
        fullWidth
        maxWidth={maxWidth}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
        data-testid={testId ? `${testId}ModalWrapper` : "modalWrapper"}
        PaperProps={{ 
          style: { borderRadius: 0 }
        }}
      >
        <MyDialogTitle id="customized-dialog-title" onClose={handleClose}>
          {title}
        </MyDialogTitle>
        <DialogContent
          dividers={hideDivider !== true}
          data-testid={testId ? `${testId}ModalContent` : "modalContent"}
          sx={{ padding: theme.spacing(3) }}
        >
          {children}
        </DialogContent>
        <DialogActions sx={{ justifyContent: buttonsJustify }}>
          {dialogActions.map(
            (item) =>
              !item.hidden && (
                <Button
                  key={item.label.replace(/\s/g, "")}
                  autoFocus={item.autoFocus}
                  sx={{
                    margin: 0,
                    display: "flex",
                    justifyContent: "center",
                  }}
                  variant={item.variant}
                  color="primary"
                  disabled={!item.canExecute || item.disabled}
                  onClick={item.execute}
                  data-testid={`${item.label.replace(/ /g, "")}Input`}
                >
                  {item.label}
                  {item.labelIcon && (<item.labelIcon />)}
                </Button>
              )
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
